import React, { useMemo, useRef } from 'react';
import useMeetingLayout from 'flow/hooks/useMeetingLayout';
import { useAppSelector } from 'flow/hooks/useRedux';
import {
    selectPinnedFeedNames,
    selectScreenShareAspect,
    selectSpeakingMembers,
    selectVideoSenderScreenShareActive,
    selectVideoShowScreenShareInGrid,
} from 'flow/selectors/video-selectors';
import { Rect } from 'flow/utils/meeting-layout';
import MeetingTileContext, {
    MeetingTileContextValue,
} from './MeetingTileContext';
import PresentingBadge from './PresentingBadge';
import PositionedTile from './tiles/PositionedTile';
import TileMemberFeed from './tiles/TileMemberFeed';
import TileLocalFeed from './tiles/TileLocalFeed';
import TileFeedOverflow from './tiles/TileFeedOverflow';
import TileLocalStageScreenShare from './tiles/TileLocalStageScreenShare';
import TileRemoteStageScreenShare from './tiles/TileRemoteStageScreenShare';
import MeetingLocalVideo from '../MeetingLocalVideo';
import { sliceUUIDFromUserName } from 'flow/utils/meeting-utils';
import {
    createStickyOrderState,
    FeedEntry,
    FeedMember,
    memberDisplayName,
    memberKey,
    reorderSticky,
} from './meeting-tile-utils';

interface MeetingMainAreaProps {
    /** Ordered participant feeds (non screen-share), from useMemberManagement. */
    feedMembers: FeedMember[];
    /**
     * Ordered remote members currently presenting (screen-sharing). Their screen
     * takes the stage; their camera rides along as a feed in the strip.
     */
    presenters: FeedMember[];
    /**
     * True (uncapped) count of feed-eligible members. `feedMembers` is capped
     * at GRID_LIMIT for rendering; this lets the "+N" overflow indicator count
     * everyone not shown, not just those the layout engine hides.
     */
    totalFeedCount: number;
    shouldShowSenderInGrid: boolean;
    isCompact: boolean;
    /**
     * Compact dock (thumbnail/sidebar): a faces-only FEED grid — no stage, no
     * pinning — since the dock is too narrow for a stage + strip split.
     */
    isThumbnail?: boolean;
}

/**
 * Engine-driven Main area: lays out participant feeds (FEED mode) or a stage +
 * feed strip (SPLIT mode) from rects computed against the measured container.
 * Screen share takes the stage; otherwise pinned feeds form the stage.
 *
 * Every participant tile (pinned-on-stage and in-strip) is rendered from a
 * single keyed list, so pinning/unpinning only repositions a tile (stage rect
 * <-> strip rect) rather than moving it between separate components — the
 * latter would remount it and tear down its <video>/stream for a beat.
 */
const MeetingMainArea: React.FC<MeetingMainAreaProps> = ({
    feedMembers,
    presenters,
    totalFeedCount,
    shouldShowSenderInGrid,
    isCompact,
    isThumbnail = false,
}) => {
    const pinnedFeedNames = useAppSelector(selectPinnedFeedNames);
    const shouldShowScreenShareInGrid = useAppSelector(
        selectVideoShowScreenShareInGrid,
    );
    const senderScreenShareActive = useAppSelector(
        selectVideoSenderScreenShareActive,
    );
    const screenAspect = useAppSelector(selectScreenShareAspect);
    const speakingMembers = useAppSelector(selectSpeakingMembers);
    // Persistent sticky-order state for the on-screen window (see reorderSticky).
    const stickyVisibleOrder = useRef(createStickyOrderState());

    const screenShareOnStage =
        shouldShowScreenShareInGrid &&
        (senderScreenShareActive || presenters.length > 0);
    // In the narrow dock a shared screen takes over the whole pane: it fills the
    // stage and the participant faces step aside (they return in fullscreen or
    // once the share stops). Elsewhere the dock stays a plain faces grid.
    const dockShareTakeover = isThumbnail && screenShareOnStage;

    // The presenter shown in the badge: the on-stage remote sharer, or the
    // local user (undefined name) when it's our own screen on the stage.
    const presenter = presenters[0];
    const presenterName = presenter
        ? sliceUUIDFromUserName(memberDisplayName(presenter))
        : undefined;

    // Pinned feeds form the stage only when no screen share is on it; otherwise
    // they demote into the feed strip with everyone else. This holds in the
    // dock too — a pin there resolves to a vertical stage + strip.
    const pinnedMembers = screenShareOnStage
        ? []
        : feedMembers.filter((m) => pinnedFeedNames.has(memberDisplayName(m)));
    const showAllInStrip = screenShareOnStage;
    // While a screen owns the stage, presenters also join the strip — their screen
    // is on the stage, their camera rides along as a strip feed. (Own camera is the
    // local tile, added below.) Presenters lead the remote feeds so that, when the
    // strip overflows into "+N", an idle remote is dropped before an active
    // presenter's camera: their face is the most relevant one here and isn't shown
    // anywhere else (the stage shows their screen, not them).
    const stripFaceMembers = showAllInStrip
        ? presenters.concat(feedMembers)
        : feedMembers.filter((m) => !pinnedFeedNames.has(memberDisplayName(m)));
    // The dock take-over shows the screen alone — no faces strip.
    const stripMembers = dockShareTakeover ? [] : stripFaceMembers;

    // Strip feed list: the local tile first, then remote feeds. Overflow drops
    // the *last* cells into the "+N" indicator, so leading with the local tile
    // keeps the user's own self-view from being the first thing hidden — a
    // remote is dropped before it, and "+N" then counts hidden others.
    const feedEntries: FeedEntry[] = [];
    if (shouldShowSenderInGrid && !dockShareTakeover) {
        feedEntries.push({ kind: 'local', key: 'local-feed' });
    }
    stripMembers.forEach((member, index) => {
        feedEntries.push({
            kind: 'remote',
            member,
            key: memberKey(member, index),
        });
    });

    // The engine sizes the grid from how many face objects we actually have to
    // render (feedEntries) but counts overflow against the true total, so "+N"
    // reflects everyone not shown — including members past the GRID_LIMIT cap,
    // not just those the container hides. Pinned feeds sit on the stage (shown),
    // so they don't count toward the strip's hidden total.
    const localFeedCount = shouldShowSenderInGrid && !dockShareTakeover ? 1 : 0;
    // Presenters' cameras were added to the strip above but aren't in
    // totalFeedCount (which counts non-screen-share faces), so add them back to
    // keep the "+N" overflow total accurate.
    const presenterCamerasInStrip = showAllInStrip ? presenters.length : 0;
    const remoteFeedTotal = dockShareTakeover
        ? 0
        : totalFeedCount - pinnedMembers.length + presenterCamerasInStrip;
    const stripTrueTotal = localFeedCount + remoteFeedTotal;

    const { ref, layout } = useMeetingLayout({
        feedCount: stripTrueTotal,
        maxVisibleFeeds: feedEntries.length,
        pinnedCount: pinnedMembers.length,
        screenShareActive: screenShareOnStage,
        screenAspect,
        // The dock uses smaller feed tiles…
        compact: isThumbnail,
        // …and stays a faces-only grid unless something claims the stage — a
        // screen share, or a pinned participant (→ vertical stage + strip).
        feedOnly:
            isThumbnail && !screenShareOnStage && pinnedMembers.length === 0,
    });

    // Now that the layout has decided how many feeds actually fit on screen,
    // keep active speakers within the visible window — pulling an off-screen
    // speaker in and letting it stay there (sticky) until a newer speaker
    // displaces the longest-idle occupant. Done here, not in useMemberManagement,
    // because only the layout engine knows the true on-screen count (smaller than
    // the GRID_LIMIT cap in a narrow window). The local self-view leads and is
    // never reordered, so sticky ordering runs over the remote feeds only. Tile
    // identity is keyed off the member (not the slot), so a swap never remounts a
    // tile / tears down its <video>.
    const localEntries = feedEntries.filter((entry) => entry.kind === 'local');
    const remoteEntries = feedEntries.filter(
        (entry) => entry.kind === 'remote',
    );
    const visibleFeedEntries: FeedEntry[] = [
        ...localEntries,
        ...reorderSticky(
            stickyVisibleOrder.current,
            remoteEntries,
            Math.max(0, layout.visibleFeedCount - localEntries.length),
            (entry) => entry.key,
            (entry) =>
                entry.kind === 'remote' &&
                speakingMembers.has(memberDisplayName(entry.member)),
        ),
    ];

    const tileContext: MeetingTileContextValue = useMemo(
        () => ({
            isCompact,
            // No pin control while a screen owns the stage: a shared screen
            // takes the whole stage, so pinning a face there would silently do
            // nothing. Hide it rather than toggle a pin that has no effect.
            pinControlEnabled: !screenShareOnStage,
        }),
        [isCompact, screenShareOnStage],
    );

    // Every participant tile — pinned (stage rects) and feed (strip rects) —
    // collected into one keyed list so a member keeps the same instance (and
    // its live <video>) when pinning moves it between the stage and the strip.
    const memberTiles: { key: string; rect: Rect; node: React.ReactNode }[] =
        [];
    (layout.pinnedTiles ?? []).forEach((tile) => {
        const member = pinnedMembers[tile.index];
        if (member) {
            memberTiles.push({
                key: memberKey(member, tile.index),
                rect: tile.rect,
                node: <TileMemberFeed member={member} />,
            });
        }
    });
    layout.feedTiles.forEach((tile) => {
        if (layout.overflowCount > 0 && tile.index >= layout.visibleFeedCount) {
            memberTiles.push({
                key: 'feed-overflow',
                rect: tile.rect,
                node: <TileFeedOverflow count={layout.overflowCount} />,
            });
            return;
        }
        const entry = visibleFeedEntries[tile.index];
        if (!entry) {
            return;
        }
        memberTiles.push(
            entry.kind === 'local'
                ? {
                      key: 'local-feed',
                      rect: tile.rect,
                      node: (
                          <TileLocalFeed
                              isSecondary={senderScreenShareActive}
                          />
                      ),
                  }
                : {
                      key: memberKey(entry.member, tile.index),
                      rect: tile.rect,
                      node: <TileMemberFeed member={entry.member} />,
                  },
        );
    });

    return (
        <MeetingTileContext.Provider value={tileContext}>
            <div ref={ref} className="relative h-full w-full">
                {screenShareOnStage && (
                    <PresentingBadge presenterName={presenterName} />
                )}
                {/* Screen share owns the stage; it doesn't move between stage
                    and strip, so it's rendered on its own (stable key). The
                    stage share renders through one role-based tile whether the
                    source is local or remote — camera concerns stay elsewhere. */}
                {screenShareOnStage && layout.stage && (
                    <PositionedTile key="screen-share" rect={layout.stage}>
                        {presenters.length > 0 ? (
                            <TileRemoteStageScreenShare
                                member={presenters[0]}
                            />
                        ) : (
                            <TileLocalStageScreenShare />
                        )}
                    </PositionedTile>
                )}
                {memberTiles.map((tile) => (
                    <PositionedTile key={tile.key} rect={tile.rect}>
                        {tile.node}
                    </PositionedTile>
                ))}
                {/* When the sender opts out of the grid, their camera shows as a
                    corner PiP (click it to dock back into the grid) — both when
                    idle and while screen-sharing. During a share the screen owns
                    the stage and the camera rides along as its secondary feed. */}
                {!shouldShowSenderInGrid && (
                    <MeetingLocalVideo isSecondary={senderScreenShareActive} />
                )}
            </div>
        </MeetingTileContext.Provider>
    );
};

export default MeetingMainArea;
